import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightLong,
  faGlobe,
  faHouse,
  faLock,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../store';
import { setShowManageGroups } from '../reducers/uiReducer';
import { setParams } from '../reducers/paramsReducer';
import { fetchGroups, createGroup } from '../reducers/groupsReducer';
import { getGames } from '../reducers/gamesReducer';
import { GroupKind, GroupModel, GroupVisibility } from '../types/GroupModel';
import GroupsAPI from '../api/GroupsAPI';
import AccountsAPI from '../api/AccountsAPI';
import { ConnectedAccountModel } from '../types/ConnectedAccountModel';
import { platformLabel } from './ConnectedAccounts';

// One editable row: rename any owned group; flip visibility or delete only user-made (Custom) ones
// — My Space and Home are permanent system groups.
function GroupRow(props: {
  group: GroupModel;
  accounts: ConnectedAccountModel[];
  onAccountsChanged: (accounts: ConnectedAccountModel[]) => void;
  onChanged: () => void;
  onDeleted: (id: number) => void;
  onOpen: (token: string) => void;
  onError: (message: string) => void;
}): React.ReactNode {
  const { group, accounts, onAccountsChanged, onChanged, onDeleted, onOpen, onError } = props;
  const [syncBusy, setSyncBusy] = useState(false);

  // Which connected accounts sync into this group. An account syncs into exactly one group: null
  // means My Space, so the Personal row owns the unassigned ones.
  const syncsHere = (a: ConnectedAccountModel) =>
    a.AutoSync &&
    (a.SyncGroupId === group.Id || (a.SyncGroupId == null && group.Kind === GroupKind.Personal));

  const setSync = async (a: ConnectedAccountModel, on: boolean) => {
    setSyncBusy(true);
    try {
      const next = on
        ? group.Kind === GroupKind.Personal
          ? { autoSync: true, clearGroup: true }
          : { autoSync: true, syncGroupId: group.Id }
        : { autoSync: false };
      onAccountsChanged(await AccountsAPI.update(a.Platform, next));
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSyncBusy(false);
    }
  };
  const [name, setName] = useState(group.Name);
  const [busy, setBusy] = useState(false);
  const dirty = name.trim() !== group.Name && name.trim() !== '';
  const isCustom = group.Kind === GroupKind.Custom;

  const saveName = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      await GroupsAPI.update(group.Id, { name: name.trim() });
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleVisibility = async (makePublic: boolean) => {
    setBusy(true);
    try {
      await GroupsAPI.update(group.Id, {
        visibility: makePublic ? GroupVisibility.Public : GroupVisibility.Private,
      });
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${group.Name}"? The games stay in your other groups.`)) return;
    setBusy(true);
    try {
      await GroupsAPI.remove(group.Id);
      onDeleted(group.Id);
    } catch (e) {
      onError((e as Error).message);
      setBusy(false);
    }
  };

  const icon =
    group.Kind === GroupKind.Home
      ? faHouse
      : group.Visibility === GroupVisibility.Public
        ? faGlobe
        : faLock;

  return (
    <div className="cm-manage-group-row">
      <FontAwesomeIcon icon={icon} className="cm-manage-group-icon" fixedWidth />
      <Form.Control
        size="sm"
        value={name}
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onBlur={saveName}
        onKeyDown={(e) => {
          if (e.key === 'Enter') saveName();
        }}
      />
      <Badge bg="secondary" className="cm-manage-group-count">
        {group.GameCount}
      </Badge>
      <Button
        variant="link"
        className="cm-manage-group-open"
        title={`Go into ${group.Name}`}
        disabled={busy}
        onClick={() => onOpen(group.Token)}
      >
        <FontAwesomeIcon icon={faArrowRightLong} />
      </Button>
      {isCustom ? (
        <Form.Check
          type="switch"
          className="cm-manage-group-vis"
          label={group.Visibility === GroupVisibility.Public ? 'Public' : 'Private'}
          checked={group.Visibility === GroupVisibility.Public}
          disabled={busy}
          onChange={(e) => toggleVisibility(e.target.checked)}
        />
      ) : (
        <span className="cm-manage-group-fixed">
          {group.Kind === GroupKind.Home ? 'Home page' : 'Private'}
        </span>
      )}
      {isCustom ? (
        <Button variant="link" className="cm-manage-group-del" disabled={busy} onClick={del}>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      ) : (
        <span className="cm-manage-group-del-spacer" />
      )}
      {accounts.length > 0 && group.Kind !== GroupKind.Home ? (
        <div className="cm-manage-group-sync">
          <span className="cm-manage-group-sync-label">Auto-sync from</span>
          {accounts.map((a) => (
            <Form.Check
              key={a.Platform}
              type="checkbox"
              id={`sync-${group.Id}-${a.Platform}`}
              className="cm-manage-group-sync-check"
              label={`${platformLabel(a.Platform)} · ${a.Username}`}
              checked={syncsHere(a)}
              disabled={busy || syncBusy}
              onChange={(e) => setSync(a, e.target.checked)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function GamesManageGroupsModal(): React.ReactNode {
  const dispatch = useAppDispatch();
  const { showManageGroups } = useAppSelector((state) => state.uiReducer);
  const { groups } = useAppSelector((state) => state.groupsReducer);
  const { params } = useAppSelector((state) => state.paramsReducer);
  const [error, setError] = useState<string>();
  const [newName, setNewName] = useState('');
  const [newPublic, setNewPublic] = useState(false);
  const [creating, setCreating] = useState(false);

  const mine = useMemo(() => groups.filter((g) => g.IsOwner), [groups]);
  // Connected accounts, for the per-group "Auto-sync from" row. Loaded when the modal opens.
  const [accounts, setAccounts] = useState<ConnectedAccountModel[]>([]);
  useEffect(() => {
    if (!showManageGroups) return;
    AccountsAPI.list()
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, [showManageGroups]);

  const refresh = () => {
    dispatch(fetchGroups());
    dispatch(getGames(params));
  };

  // "Go into" a group: point the games list at it (by its URL token) and close the modal. The
  // params change reloads the list (GamesContainer), so there's no separate fetch here.
  const onOpen = (token: string) => {
    dispatch(setParams({ group: token }));
    dispatch(setShowManageGroups(false));
  };

  const onDeleted = (id: number) => {
    // If the group being viewed was deleted, fall back to My Space (or Home). Match on the deleted
    // group's token, since the view is addressed by token now.
    const deleted = groups.find((g) => g.Id === id);
    if (deleted != null && params.group === deleted.Token) {
      const mySpace = groups.find((g) => g.Kind === GroupKind.Personal && g.IsOwner);
      const home = groups.find((g) => g.Kind === GroupKind.Home);
      dispatch(setParams({ group: mySpace?.Token ?? home?.Token ?? null }));
    }
    refresh();
  };

  const onCreate = async () => {
    const name = newName.trim();
    if (name === '') return;
    setCreating(true);
    try {
      await dispatch(
        createGroup({
          name,
          visibility: newPublic ? GroupVisibility.Public : GroupVisibility.Private,
        }),
      ).unwrap();
      setNewName('');
      setNewPublic(false);
      dispatch(getGames(params));
    } catch (e) {
      setError((e as Error).message ?? 'Could not create the group.');
    } finally {
      setCreating(false);
    }
  };

  if (!showManageGroups) {
    return null;
  }

  return (
    <Modal show={showManageGroups} onHide={() => dispatch(setShowManageGroups(false))}>
      <Modal.Header closeButton>
        <Modal.Title>Manage groups</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="cm-import-help">
          Rename a group, make a collection public so others can see it, or delete one you no longer
          need. Deleting a group keeps the games. They stay in your other groups.
          {accounts.length > 0
            ? ' Tick an account under a group and its new games are pulled in there automatically.'
            : ''}
        </p>
        {error != null ? (
          <Alert variant="danger" dismissible onClose={() => setError(undefined)}>
            {error}
          </Alert>
        ) : null}

        <div className="cm-manage-groups">
          {mine.map((g) => (
            <GroupRow
              key={g.Id}
              group={g}
              accounts={accounts}
              onAccountsChanged={setAccounts}
              onChanged={refresh}
              onDeleted={onDeleted}
              onOpen={onOpen}
              onError={setError}
            />
          ))}
        </div>

        <hr />
        <FormLabelledCreate
          newName={newName}
          setNewName={setNewName}
          newPublic={newPublic}
          setNewPublic={setNewPublic}
          creating={creating}
          onCreate={onCreate}
        />
      </Modal.Body>
    </Modal>
  );
}

function FormLabelledCreate(props: {
  newName: string;
  setNewName: (v: string) => void;
  newPublic: boolean;
  setNewPublic: (v: boolean) => void;
  creating: boolean;
  onCreate: () => void;
}): React.ReactNode {
  const { newName, setNewName, newPublic, setNewPublic, creating, onCreate } = props;
  return (
    <div className="cm-manage-create">
      <Form.Label className="cm-manage-create-label">New group</Form.Label>
      <div className="cm-import-group-row">
        <Form.Control
          placeholder="e.g. Caruana's games, My French repertoire"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onCreate();
          }}
        />
        <Button onClick={onCreate} disabled={creating || newName.trim() === ''}>
          Create
        </Button>
      </div>
      <Form.Check
        type="switch"
        className="mt-2"
        label={
          <span>
            <FontAwesomeIcon icon={newPublic ? faGlobe : faLock} className="me-2" />
            {newPublic ? 'Public. Anyone can see it' : 'Private. Only you'}
          </span>
        }
        checked={newPublic}
        onChange={(e) => setNewPublic(e.target.checked)}
      />
    </div>
  );
}
